import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import {
    Card,
    Grid,
    Icon,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    Divider,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Typography,
    Box,
    CircularProgress
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isoWeek);
import axios from "axios";
import { CloudUpload, Delete, Visibility, PlayCircle, Image, PictureAsPdf, Close, InsertDriveFile, GetApp, FileDownload, YouTube, Link, Description, Download } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MaterialReactTable } from "material-react-table";
import { compressImage } from "utils/imageCompression";

const WeeklyReportManager = () => {
    const location = useLocation();


    const getWeeksForMonth = (date) => {
        const startOfMonth = dayjs(date).startOf("month");
        const weeks = [];

        // Find the first Monday of the month
        let currentMonday = startOfMonth.isoWeekday(1);
        if (currentMonday.isBefore(startOfMonth, "day")) {
            currentMonday = currentMonday.add(7, "days");
        }

        // Generate exactly 4 weeks
        for (let i = 1; i <= 4; i++) {
            const weekStart = currentMonday;
            const weekEnd = currentMonday.add(6, "days");
            weeks.push({
                value: i,
                label: `${weekStart.format("D")} - ${weekEnd.format("D")} (${i === 1 ? "1st" : i === 2 ? "2nd" : i === 3 ? "3rd" : "4th"} Week)`,
                start: weekStart,
                end: weekEnd,
            });
            currentMonday = currentMonday.add(7, "days");
        }
        return weeks;
    };

    const getCurrentWeekIndex = (date) => {
        const weeks = getWeeksForMonth(date);
        const today = dayjs();
        const found = weeks.find((w) => (today.isAfter(w.start) || today.isSame(w.start, "day")) && (today.isBefore(w.end) || today.isSame(w.end, "day")));
        return found ? found.value : 1;
    };

    const formik = useFormik({
        initialValues: {
            date: dayjs(),
            weekNumber: getCurrentWeekIndex(dayjs()),
            reportFiles: [],
            supportingMedia: [],
        },
        validationSchema: Yup.object({
            date: Yup.mixed().required("Month and Year is required"),
            weekNumber: Yup.number().required("Week number is required").min(1).max(5),
            reportFiles: Yup.array().min(1, "At least one Weekly Report is required")
                .of(Yup.mixed().test("fileType", "Only PDF and Word files are allowed", (value) => {
                    if (!value) return false;
                    const type = typeof value === 'string' ? value.split('.').pop() : value.type;
                    if (typeof value === 'string') return true;
                    return [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    ].includes(value.type);
                }))

        }),
        onSubmit: async (values) => {
            const selectedMonth = values.date.month() + 1;
            const selectedYear = values.date.year();

            // Check for monthly limit
            const reportsInMonth = reports.filter(r =>
                dayjs(r.uploadDate).month() + 1 === selectedMonth &&
                dayjs(r.uploadDate).year() === selectedYear
            );

            if (reportsInMonth.length >= 4 && !isEditMode) {
                setNotification({
                    open: true,
                    message: "Maximum of 4 reports per month reached.",
                    severity: "error"
                });
                return;
            }

            // Check for duplicate week
            const isWeekAlreadySubmitted = reports.some(r => r.weekNumber === values.weekNumber);
            if (isWeekAlreadySubmitted && !isEditMode) {
                setNotification({
                    open: true,
                    message: `A report for Week ${values.weekNumber} has already been submitted.`,
                    severity: "error"
                });
                return;
            }

            try {
                const payload = {
                    month: selectedMonth,
                    year: selectedYear,
                    weekNumber: values.weekNumber,
                    reportPdf: JSON.stringify(values.reportFiles.map(f => typeof f === 'string' ? f : f.url)),
                    supportingMedia: [
                        ...existingMediaUrls.map(m => m.url),
                        ...values.supportingMedia.map(m => m.url)
                    ].join(",") || null
                };

                if (isEditMode) {
                    await axios.put(`${process.env.REACT_APP_API_URL}/reports/weekly/${editingReportId}`, payload, {
                        withCredentials: true
                    });
                    setNotification({ open: true, message: "Report updated successfully!", severity: "success" });
                } else {
                    await axios.post(`${process.env.REACT_APP_API_URL}/reports/weekly/upload`, payload, {
                        withCredentials: true
                    });
                    setNotification({ open: true, message: "Report uploaded successfully!", severity: "success" });
                }

                handleCloseUploadDialog();
                fetchReports();
            } catch (error) {
                setNotification({
                    open: true,
                    message: error.response?.data?.message || "Operation failed. Please try again.",
                    severity: "error"
                });
            } finally {
                formik.setSubmitting(false);
            }
        },
    });

    const handleCloseUploadDialog = () => {
        setOpenUploadDialog(false);
        setIsEditMode(false);
        setEditingReportId(null);
        setEditingReport(null);
        setExistingMediaUrls([]); // Reset existing media URLs
        formik.resetForm({
            values: {
                ...formik.initialValues,
                date: selectedDate, // Reset back to currently filtered date
                weekNumber: getCurrentWeekIndex(selectedDate),
            }
        });
    };

    const handleOpenUploadDialog = () => {
        formik.setFieldValue("date", selectedDate);
        formik.setFieldValue("weekNumber", getCurrentWeekIndex(selectedDate));
        setOpenUploadDialog(true);
    };



    const [reports, setReports] = useState([]);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [notification, setNotification] = useState({ open: false, message: "", severity: "success" });
    const [viewPdfUrl, setViewPdfUrl] = useState(null);
    const [message, setMessage] = useState({
        status: "",
        title: "",
        msg: "",
    });
    const [tempFileUrl, setTempFileUrl] = useState(null);
    const [videoUrl, setVideoUrl] = useState("");

    // Range: May of (Current Year - 1) to March of (Current Year + 1)
    const minDateLimit = dayjs().subtract(1, "year").month(4).startOf("month");
    const maxDateLimit = dayjs().add(1, "year").month(2).endOf("month");

    const me = async () => {
        const response = await axios({
            method: "post",
            url: `${process.env.REACT_APP_API_URL}/auth/me`,
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true,
        });
        // console.log("🚀 ~ fetchUsers ~ response:", response);
        return response.data;
    };

    const {
        data: meData,
        isLoading: meLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ["me"], // Unique key for the query
        queryFn: me, // The async function to run
        retry: false,
    });
    console.log("meData", meData)

    const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingReportId, setEditingReportId] = useState(null);
    const [editingReport, setEditingReport] = useState(null);
    const [existingMediaUrls, setExistingMediaUrls] = useState([]);
    const [isUploadingReport, setIsUploadingReport] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);

    // Dummy data for initial view
    const [isLoading, setIsLoading] = useState(false);


    const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - 2 + i);

    useEffect(() => {
        fetchReports();
    }, [selectedDate]);

    // Handle filter from navigation state (Notification click)
    useEffect(() => {
        if (location.state?.month && location.state?.year) {
            // Set date
            const newDate = dayjs().year(location.state.year).month(location.state.month - 1);
            setSelectedDate(newDate);

            // Clear state to avoid re-applying on every render or back navigation
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);



    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/reports/weekly`, {
                params: { month: selectedDate.month() + 1, year: selectedDate.year() },
                withCredentials: true
            });

            const reportsData = response.data.data.map(r => ({
                id: r.id,
                weekNumber: r.week_number,
                month: r.month,
                year: r.year,
                uploadDate: r.created_at,
                pdfUrl: r.report_pdf,
                uploaderName: r.uploader_name,
                reportFiles: (() => {
                    try {
                        const urls = JSON.parse(r.report_pdf);
                        return Array.isArray(urls) ? urls : [r.report_pdf];
                    } catch (e) {
                        return [r.report_pdf];
                    }
                })(),
                media: (r.supporting_media || []).map(url => {
                    const lowerUrl = url.toLowerCase();
                    let type = "image";
                    if (lowerUrl.match(/\.(mp4|mov|quicktime)$/)) type = "video";
                    else if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) type = "youtube";
                    else if (lowerUrl.includes("drive.google.com")) type = "drive";
                    else if (lowerUrl.endsWith(".pdf")) type = "pdf";
                    else if (lowerUrl.endsWith(".doc") || lowerUrl.endsWith(".docx")) type = "word";
                    else if (!lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) type = "link";

                    return {
                        url: url,
                        type: type,
                        name: url.split('/').pop().split('-').slice(1).join('-') || url.split('/').pop()
                    };
                })
            }));

            setReports(reportsData);
        } catch (error) {
            console.error("Error fetching reports", error);
            setNotification({ open: true, message: "Failed to fetch reports", severity: "error" });
        } finally {
            setIsLoading(false);
        }
    };



    const handleClosePdf = () => setViewPdfUrl(null);

    const handlePreview = (url) => {
        if (!url) return;
        const cleanUrl = url.split('?')[0].toLowerCase();
        const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

        if (isWord) {
            const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
            setViewPdfUrl(officeUrl);
        } else {
            setViewPdfUrl(url);
        }
    };

    const handleDownload = async (url, title) => {
        if (!url) return;
        try {
            const cleanUrl = url.split('?')[0].toLowerCase();
            const originalExt = cleanUrl.split('.').pop();
            const response = await fetch(url);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `${title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.${originalExt}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setMessage({ status: "success", title: "Success", msg: "File downloaded successfully!" });
        } catch (error) {
            console.error("Download failed", error);
            setMessage({ status: "error", title: "Error", msg: "Download failed. Please try again." });
        }
    };

    const DocumentActions = ({ url, title, isPdf, isWord }) => {
        if (!url) return <MDTypography variant="caption" color="text">-</MDTypography>;
        return (
            <MDBox display="flex" gap={1} alignItems="center">
                {isPdf ? <PictureAsPdf sx={{ fontSize: '1rem', color: '#f44336' }} /> : (isWord ? <Description sx={{ fontSize: '1rem', color: '#1976d2' }} /> : null)}
                <Tooltip title="View">
                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => handlePreview(url)}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, width: 24, height: 24 }}
                    >
                        <Visibility sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                    <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleDownload(url, title)}
                        sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' }, width: 24, height: 24 }}
                    >
                        <Download sx={{ fontSize: '0.875rem' }} />
                    </IconButton>
                </Tooltip>
            </MDBox>
        );
    };

    DocumentActions.propTypes = {
        url: PropTypes.string,
        title: PropTypes.string,
        isPdf: PropTypes.bool,
        isWord: PropTypes.bool,
    };

    const handleEdit = (report) => {
        console.log(report)
        setIsEditMode(true);
        setEditingReportId(report.id);
        setEditingReport(report);
        setExistingMediaUrls(report.media || []);
        setTempFileUrl(report.pdfUrl); // Set existing URL for preview

        const reportDate = dayjs().year(report.year).month(report.month - 1);
        setSelectedDate(reportDate); // Sync main date to fetch correct reports
        formik.setValues({
            date: reportDate,
            weekNumber: report.weekNumber,
            reportFiles: report.reportFiles || [report.pdfUrl],
            supportingMedia: [],
        });

        setOpenUploadDialog(true);
    };

    const currentWeeks = getWeeksForMonth(dayjs());
    const initialWeek = currentWeeks.findIndex(w => {
        const today = dayjs();
        const startOfMonth = today.startOf("month");
        let curr = startOfMonth.startOf("isoWeek");
        for (let i = 1; i <= 6; i++) {
            if (today.isBefore(curr.add(7, "days"))) return i === i; // This is a bit complex, let's simplify
        }
        return 1;
    });

    // Simple current week calculation based on the same logic



    // Remove the single reportFile preview logic as we'll handle multiple below

    const handleFileChange = async (event) => {
        const files = Array.from(event.currentTarget.files);
        if (files.length === 0) return;

        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        const validFiles = files.filter(file => allowedTypes.includes(file.type));
        if (validFiles.length !== files.length) {
            setNotification({ open: true, message: "Only PDF and Word files are allowed", severity: "error" });
        }

        setIsUploadingReport(true);
        try {
            const uploadedFiles = [];
            for (const file of validFiles) {
                const MAX_FILE_SIZE = file.type === "application/pdf" ? 25 * 1024 * 1024 : 5 * 1024 * 1024;
                if (file.size > MAX_FILE_SIZE) {
                    setNotification({ open: true, message: `File ${file.name} exceeds size limit`, severity: "error" });
                    continue;
                }

                const formData = new FormData();
                formData.append("file", file);
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/reports/upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true
                });
                uploadedFiles.push({
                    url: response.data.url,
                    name: file.name,
                    type: file.type
                });
            }
            formik.setFieldValue("reportFiles", [...formik.values.reportFiles, ...uploadedFiles]);
        } catch (error) {
            console.error("Upload failed", error);
            setNotification({ open: true, message: "File upload failed", severity: "error" });
        } finally {
            setIsUploadingReport(false);
        }
    };

    const handleMediaChange = async (event) => {
        const files = Array.from(event.currentTarget.files);
        const validFiles = files.filter(file => file.type.startsWith('image/'));

        if (validFiles.length !== files.length) {
            setNotification({
                open: true,
                message: "Only images can be uploaded here. For videos, please provide a link.",
                severity: "warning"
            });
        }

        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const validSizeFiles = validFiles.filter(file => file.size <= MAX_FILE_SIZE);

        if (validSizeFiles.length !== validFiles.length) {
            setNotification({
                open: true,
                message: "Some files were skipped because they exceed the 5MB limit.",
                severity: "warning"
            });
        }

        if (validSizeFiles.length === 0) return;

        setIsUploadingMedia(true);
        try {
            const uploadedMedia = [];
            for (const file of validSizeFiles) {
                const compressedFile = await compressImage(file);
                const formData = new FormData();
                formData.append("file", compressedFile);
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/reports/upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true
                });

                uploadedMedia.push({
                    url: response.data.url,
                    type: "image",
                    name: file.name
                });
            }
            formik.setFieldValue("supportingMedia", [...formik.values.supportingMedia, ...uploadedMedia]);
        } catch (error) {
            console.error("Media upload failed", error);
            setNotification({ open: true, message: "Media upload failed", severity: "error" });
        } finally {
            setIsUploadingMedia(false);
        }
    };

    const handleAddVideoUrl = () => {
        if (!videoUrl.trim()) return;

        // Simple URL validation
        try {
            new URL(videoUrl);
        } catch (e) {
            setNotification({ open: true, message: "Please enter a valid URL", severity: "error" });
            return;
        }

        const lowerUrl = videoUrl.toLowerCase();
        let type = "video";
        if (lowerUrl.includes("youtube.com") || lowerUrl.includes("youtu.be")) type = "youtube";
        else if (lowerUrl.includes("drive.google.com")) type = "drive";

        const videoObject = {
            url: videoUrl,
            type: type,
            name: "External Video"
        };

        formik.setFieldValue("supportingMedia", [...formik.values.supportingMedia, videoObject]);
        setVideoUrl("");
    };

    const removeMedia = (index) => {
        const newMedia = [...formik.values.supportingMedia];
        newMedia.splice(index, 1);
        formik.setFieldValue("supportingMedia", newMedia);
    };

    const handleExportExcel = () => {

        if (!reports || reports.length === 0) {
            setNotification({
                open: true,
                message: "No data to export",
                severity: "warning",
            });
            return;
        }

        const wb = XLSX.utils.book_new();

        // Max media count
        const maxMedia = Math.max(...reports.map(r => (r.media ? r.media.length : 0)));

        const mediaHeaders = Array.from({ length: maxMedia }, (_, i) => `Media ${i + 1}`);

        // Generate period label summary for the whole month
        const weeksInfo = getWeeksForMonth(selectedDate);
        const rangesSummary = weeksInfo.map(w => `W${w.value}: ${w.label.split(' (')[0]}`).join(', ');

        const header = [
            "S.No",
            "Reporting Period",
            "Week",
            "Submitted On",
            isAdmin ? "Uploaded By" : null,
            "Main Report URL",
            ...mediaHeaders,
        ].filter(Boolean);

        // 🔍 URL sanitizer with debugs
        const sanitizeUrl = (url) => {

            if (!url) {
                return "";
            }

            const cleanedUrl = url.trim();

            if (
                cleanedUrl.includes("youtube.com") ||
                cleanedUrl.includes("youtu.be")
            ) {
                try {
                    const urlObj = new URL(cleanedUrl);

                    let videoId = "";

                    if (cleanedUrl.includes("youtu.be/")) {
                        videoId = urlObj.pathname.split("/").pop();
                    } else {
                        videoId = urlObj.searchParams.get("v");
                    }


                    const finalUrl = videoId
                        ? `https://www.youtube.com/watch?v=${videoId}`
                        : cleanedUrl;

                    return finalUrl;
                } catch (error) {
                    console.error("❌ URL parsing failed:", error);
                    return cleanedUrl;
                }
            }

            return cleanedUrl;
        };

        const data = reports.map((report, index) => {

            const reportMonthYear = dayjs().month(report.month - 1).year(report.year);
            const reportWeeks = getWeeksForMonth(reportMonthYear);
            const currentWeekInfo = reportWeeks.find(w => w.value === report.weekNumber);
            const periodLabel = `${reportMonthYear.format("MMMM YYYY")} (${currentWeekInfo?.label.split(' (')[0] || ''})`;

            const row = [
                index + 1,
                periodLabel,
                `Week ${report.weekNumber}`,
                dayjs(report.uploadDate).format("DD MMM YYYY"),
            ];

            if (isAdmin) {
                row.push(report.uploaderName || "Unknown");
            }

            // PDF link
            if (report.pdfUrl) {
                const pdfUrl = report.pdfUrl.trim();
                row.push(pdfUrl);
            } else {
                row.push("");
            }

            // Media links
            for (let i = 0; i < maxMedia; i++) {
                const mediaItem = report.media && report.media[i];

                if (mediaItem?.url) {
                    const finalUrl = sanitizeUrl(mediaItem.url);
                    row.push(finalUrl);
                } else {
                    row.push("");
                }
            }

            return row;
        });


        const ws = XLSX.utils.aoa_to_sheet([header, ...data]);

        const wscols = [{ wch: 6 }, { wch: 30 }, { wch: 10 }, { wch: 15 }];
        if (isAdmin) wscols.push({ wch: 20 });
        wscols.push({ wch: 40 });

        for (let i = 0; i < maxMedia; i++) {
            wscols.push({ wch: 25 });
        }

        ws["!cols"] = wscols;

        XLSX.utils.book_append_sheet(wb, ws, "Weekly Reports");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `Weekly_Reports_${selectedDate.format("MMM_YYYY")}.xlsx`;

        saveAs(blob, fileName);

    };


    const columns = useMemo(
        () => [
            {
                accessorKey: "weekNumber",
                header: "Week",
                Cell: ({ row }) => `Week ${row.original.weekNumber}`,
                size: 100,
            },
            {
                accessorKey: "uploadDate",
                header: "Submitted On",
                Cell: ({ row }) => dayjs(row.original.uploadDate).format("DD MMM YYYY"),
                size: 150,
            },
            ...(isAdmin ? [
                {
                    accessorKey: "uploaderName",
                    header: "Uploaded By",
                    size: 180,
                }
            ] : []),
            {
                accessorKey: "pdfUrl",
                header: "Main Report",
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const urls = row.original.reportFiles || [row.original.pdfUrl];
                    if (!urls || urls.length === 0 || !urls[0]) return "-";

                    return (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            {urls.map((url, idx) => {
                                const cleanUrl = typeof url === 'string' ? url.split('?')[0].toLowerCase() : "";
                                const isPdf = cleanUrl.endsWith('.pdf');
                                const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');
                                return (
                                    <DocumentActions
                                        key={idx}
                                        url={url}
                                        title={`Weekly_Report_Week_${row.original.weekNumber}_${idx + 1}`}
                                        isPdf={isPdf}
                                        isWord={isWord}
                                    />
                                );
                            })}
                        </Box>
                    );
                },
                size: 150,
            },
            {
                id: "media",
                header: "Supporting Media",
                enableColumnFilter: false,
                Cell: ({ row }) => {
                    const media = row.original.media;
                    return (
                        <Box display="flex" gap={1}>
                            {media && media.map((m, idx) => (
                                <Tooltip key={idx} title={m.name}>
                                    <MDBox display="flex" gap={0.5} alignItems="center">
                                        {m.type === "pdf" || m.type === "word" ? (
                                            <DocumentActions
                                                url={m.url}
                                                title={m.name}
                                                isPdf={m.type === "pdf"}
                                                isWord={m.type === "word"}
                                            />
                                        ) : (
                                            <IconButton
                                                size="small"
                                                onClick={() => window.open(m.url, '_blank')}
                                            >
                                                {m.type === "image" ? <Image color="success" /> :
                                                    m.type === "video" ? <PlayCircle color="info" /> :
                                                        m.type === "youtube" ? <YouTube color="error" /> :
                                                            m.type === "drive" ? <InsertDriveFile color="info" /> :
                                                                <Link color="info" />}
                                            </IconButton>
                                        )}
                                    </MDBox>
                                </Tooltip>
                            ))}
                            {(!media || media.length === 0) && (
                                <MDTypography variant="caption" color="text">No media</MDTypography>
                            )}
                        </Box>
                    );
                },
                size: 200,
            },
            ...(!isAdmin ? [
                {
                    id: "actions",
                    Cell: ({ row }) => {
                        const isSubmitted = reports.some(r => r.weekNumber === row.original.weekNumber);
                        if (isAdmin) {
                            return (
                                <Tooltip title="Edit">
                                    <IconButton color="info" onClick={() => handleEdit(row.original)} disabled={isUploadingReport || isUploadingMedia}>
                                        <Icon>edit</Icon>
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                        return "-";
                    },
                    size: 100,
                }
            ] : []),
        ],
        [isAdmin, isUploadingReport, isUploadingMedia, reports]
    );

    return (
        <DashboardLayout>
            <Grid item xs={12} >
                <Card>
                    <MDBox
                        mx={2}
                        mt={3}
                        py={3}
                        px={2}
                        variant="gradient"
                        bgColor="info"
                        borderRadius="lg"
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <MDTypography variant="h6" color="white">
                            Weekly Reports Management
                        </MDTypography>
                        <MDBox display="flex" gap={2}>
                            {!isAdmin && (
                                <Tooltip title={reports.some(r => r.weekNumber === getCurrentWeekIndex(selectedDate)) ? "Report already submitted for this week" : ""}>
                                    <span>
                                        <MDButton
                                            variant="outlined"
                                            color="white"
                                            onClick={handleOpenUploadDialog}
                                            disabled={reports.some(r => r.weekNumber === getCurrentWeekIndex(selectedDate))}
                                        >
                                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                            &nbsp;Add Report
                                        </MDButton>
                                    </span>
                                </Tooltip>
                            )}
                            <MDButton variant="gradient" color="success" onClick={handleExportExcel}>
                                <FileDownload sx={{ fontWeight: "bold" }} />
                                &nbsp;Export Excel
                            </MDButton>
                        </MDBox>
                    </MDBox>


                    <MDBox pt={3} px={2} pb={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Select Month and Year"
                                        views={["year", "month"]}
                                        value={selectedDate}
                                        onChange={(newValue) => setSelectedDate(newValue)}
                                        minDate={minDateLimit}
                                        maxDate={maxDateLimit}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                size: "small",
                                                InputProps: {
                                                    readOnly: true,
                                                    sx: {
                                                        fontSize: "14px",
                                                        height: "45px",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </MDBox>

                    <MDBox pt={2} px={2} pb={2}>
                        <MaterialReactTable
                            columns={columns}
                            data={reports}
                            enableRowNumbers
                            enableColumnFilters
                            layoutMode="grid"
                            enableSorting
                            enablePagination
                            state={{ isLoading }}
                            muiTableHeadProps={{
                                sx: {
                                    padding: "0px", // won't apply directly to <thead>, but useful for general structure
                                    backgroundColor: "#011F3A", // background on <thead> element itself
                                },
                            }}
                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundColor: "#004030",
                                    color: "#fff",
                                    // fontSize: '16px',
                                    fontWeight: "bold",
                                    position: "sticky", // Make header sticky
                                    top: 0, // Stick it at the top
                                    zIndex: 1, // Ensure it stays
                                    "& .MuiTableSortLabel-root": {
                                        "& .MuiTableSortLabel-icon": {
                                            color: "#fff !important",
                                        },
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "#fff !important",
                                        fontSize: "12px",
                                        "::placeholder": {
                                            color: "#e0e0e0", // Default (blurred) placeholder color
                                            opacity: 1,
                                        },
                                    },
                                    "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                                        color: "#ffffff", // Focused placeholder color
                                    },
                                    "& .MuiIconButton-root": {
                                        color: "#fff",
                                    },
                                    "& .MuiInput-underline:before": {
                                        borderBottomColor: "#e0e0e0",
                                    },
                                    "& .MuiInput-underline:after": {
                                        borderBottomColor: "#fff",
                                    },
                                    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: "#fff",
                                    },

                                    "& .MuiCheckbox-root": {
                                        color: "#fff",
                                    },
                                },
                            }}
                            muiTableBodyCellProps={{
                                sx: {
                                    padding: "8px", // reduce from default 16px
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                },
                            }}
                            muiTableBodyRowProps={({ row }) => ({
                                sx: {
                                    backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                                },
                            })}
                            renderEmptyRowsFallback={() => (
                                <MDBox py={3} display="flex" justifyContent="center">
                                    <MDTypography variant="body2" color="text">
                                        No reports found for the selected period.
                                    </MDTypography>
                                </MDBox>
                            )}
                        />
                    </MDBox>
                </Card>
            </Grid>

            {/* Upload Dialog */}
            <Dialog
                open={openUploadDialog}
                onClose={handleCloseUploadDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    },
                }}
            >
                {/* Header */}
                <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="600" color="text.primary">
                                {isEditMode ? "Edit Weekly Report" : "Weekly Report"}
                            </MDTypography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {isEditMode ? "Modify report details and documents" : "Select period and attach documents"}
                            </Typography>
                        </MDBox>
                        <IconButton
                            onClick={handleCloseUploadDialog}
                            sx={{
                                color: 'text.secondary',
                                '&:hover': { backgroundColor: 'action.hover' }
                            }}
                        >
                            <Close />
                        </IconButton>
                    </MDBox>
                </DialogTitle>

                {/* Top Selection Area (Filters) */}
                <MDBox sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-around">
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                REPORTING MONTH & YEAR <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    views={["year", "month"]}
                                    value={formik.values.date}
                                    onChange={(value) => {
                                        formik.setFieldValue("date", value);
                                        setSelectedDate(value); // Sync with background fetch
                                    }}
                                    minDate={minDateLimit}
                                    maxDate={maxDateLimit}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            InputProps: {
                                                readOnly: true,
                                                sx: {
                                                    fontSize: "14px",
                                                    backgroundColor: 'white',
                                                    height: "42px",
                                                    width: "100%",
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                REPORTING WEEK <span style={{ color: "red" }}>*</span>
                            </Typography>
                            <TextField
                                select
                                fullWidth
                                name="weekNumber"
                                size="small"
                                value={formik.values.weekNumber}
                                onChange={formik.handleChange}
                                SelectProps={{
                                    renderValue: (selected) => {
                                        const week = getWeeksForMonth(formik.values.date).find(w => w.value === selected);
                                        const isSubmitted = !isLoading && reports.some(r => r.weekNumber === selected);
                                        return (
                                            <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                                                <MDTypography variant="button" fontWeight="regular">
                                                    {week ? week.label : selected}
                                                </MDTypography>
                                                {isSubmitted && (
                                                    <Chip
                                                        label="Submitted"
                                                        size="small"
                                                        color="success"
                                                        sx={{
                                                            fontSize: '9px',
                                                            height: '18px',
                                                            backgroundColor: '#4CAF50',
                                                            color: '#fff'
                                                        }}
                                                    />
                                                )}
                                            </MDBox>
                                        );
                                    }
                                }}
                                InputProps={{
                                    sx: {
                                        fontSize: "16px",
                                        backgroundColor: 'white',
                                        height: "42px",
                                        width: "100%", // Match fullWidth of parent
                                        maxWidth: "350px", // Increase if needed for long labels
                                    }
                                }}
                            >
                                {getWeeksForMonth(formik.values.date).map((w) => {
                                    const isSubmitted = !isLoading && reports.some(r => r.weekNumber === w.value);
                                    return (
                                        <MenuItem key={w.value} value={w.value} sx={{ fontSize: "14px", display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <MDTypography variant="button" fontWeight="regular">{w.label}</MDTypography>
                                            {isSubmitted && (
                                                <Chip
                                                    label="Submitted"
                                                    size="small"
                                                    color="success"
                                                    sx={{
                                                        fontSize: '9px',
                                                        height: '18px',
                                                        backgroundColor: '#4CAF50',
                                                        color: '#fff'
                                                    }}
                                                />
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>
                    </Grid>
                </MDBox>

                {/* Content Area */}
                <DialogContent sx={{ px: 3, py: 3 }}>
                    <Grid container spacing={3} direction="column" display="flex" alignItems="center">
                        {/* Weekly Report PDF Card */}
                        <Grid item xs={12}>
                            <MDBox
                                p={2}
                                border="1px solid"
                                borderColor="grey.300"
                                borderRadius="lg"
                                bgcolor="white"
                                width="700px"
                                height="100%"
                            >
                                <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                                    <Icon color="info">description</Icon>
                                    <MDTypography variant="h6" fontWeight="600" color="text.primary">Weekly Report (PDF/Word Document) <span style={{ color: "red" }}>*</span></MDTypography>
                                    {isUploadingReport && <CircularProgress size={16} color="info" sx={{ ml: 1 }} />}
                                </MDBox>


                                {formik.values.reportFiles.length === 0 ? (
                                    <>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            component="label"
                                            startIcon={<CloudUpload />}
                                            sx={{
                                                py: 1,
                                                borderWidth: 2,
                                                borderStyle: "dashed",
                                                textTransform: "none",
                                                fontSize: "0.9rem",
                                                fontWeight: 500,
                                                color: "info.main",
                                                bgcolor: "info.50",
                                                borderColor: formik.touched.reportFiles && formik.errors.reportFiles ? 'error.main' : 'info.light',
                                                transition: "all 0.3s ease",
                                                "&:hover": {
                                                    borderWidth: 2,
                                                    backgroundColor: "info.100",
                                                    borderColor: "info.main",
                                                    transform: "translateY(-2px)",
                                                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
                                                },
                                            }}
                                        >
                                            Choose Files
                                            <input type="file" hidden multiple accept="application/pdf,.doc,.docx" onChange={handleFileChange} />
                                        </Button>
                                        <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary", textAlign: 'center' }}>
                                            Upload your official supporting PDF or Word documents
                                        </Typography>
                                    </>
                                ) : (
                                    <MDBox display="flex" flexDirection="column" gap={2}>
                                        <MDBox display="flex" justifyContent="center" mb={3}>
                                            <Button
                                                variant="contained"
                                                component="label"
                                                size="small"
                                                startIcon={<CloudUpload />}
                                                    sx={{
                                                    textTransform: "none",
                                                    backgroundColor: "info.main",
                                                    color: "white !important",
                                                    px: 3,
                                                    '&:hover': {
                                                        backgroundColor: "info.dark",
                                                    }
                                                }}
                                            >
                                                Add More Documents
                                                <input type="file" hidden multiple accept="application/pdf,.doc,.docx" onChange={handleFileChange} />
                                            </Button>
                                        </MDBox>

                                        {formik.values.reportFiles.map((file, idx) => (
                                            <MDBox
                                                key={idx}
                                                sx={{
                                                    p: 2,
                                                    border: "2px solid",
                                                    borderColor: "success.light",
                                                    borderRadius: 2.5,
                                                    backgroundColor: "success.50",
                                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                                }}
                                            >
                                                <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                                                    <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                                        <MDBox
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                borderRadius: 1.5,
                                                                backgroundColor: "success.main",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                color: "white"
                                                            }}
                                                        >
                                                            <Icon>description</Icon>
                                                        </MDBox>
                                                        <MDBox sx={{ flex: 1, minWidth: 0 }}>
                                                            <MDTypography variant="button" fontWeight="bold" sx={{ display: 'block', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {typeof file === 'string' ? file.split('/').pop() : file.name}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" color="success">
                                                                Selected successfully
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <IconButton size="small" onClick={() => {
                                                        const newFiles = [...formik.values.reportFiles];
                                                        newFiles.splice(idx, 1);
                                                        formik.setFieldValue("reportFiles", newFiles);
                                                    }} sx={{ color: "error.main" }}>
                                                        <Close />
                                                    </IconButton>
                                                </MDBox>

                                                <MDBox
                                                    sx={{
                                                        border: "1px solid",
                                                        borderColor: "grey.300",
                                                        borderRadius: 1.5,
                                                        overflow: "hidden",
                                                        backgroundColor: "white",
                                                        height: "200px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    {(typeof file === 'string' ?
                                                        file.toLowerCase().endsWith('.pdf') :
                                                        file?.type === 'application/pdf') ? (
                                                        <iframe
                                                            src={typeof file === 'string' ? file : file.url}
                                                            width="100%"
                                                            height="100%"
                                                            style={{ border: "none", display: "block" }}
                                                            title="PDF Preview"
                                                        />
                                                    ) : (
                                                        <MDBox textAlign="center" p={2}>
                                                            <InsertDriveFile sx={{ fontSize: 48, color: "info.main", mb: 1 }} />
                                                            <MDTypography variant="button" fontWeight="medium" display="block">
                                                                Word Document
                                                            </MDTypography>
                                                        </MDBox>
                                                    )}
                                                </MDBox>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                )}

                                {formik.touched.reportFiles && formik.errors.reportFiles && formik.values.reportFiles.length === 0 && (
                                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                                        {formik.errors.reportFiles}
                                    </Typography>
                                )}
                            </MDBox>
                        </Grid>

                        {/* Supporting Media Card */}
                        <Grid item xs={12}>
                            <MDBox
                                p={1}
                                border="1px solid"
                                borderColor="grey.300"
                                borderRadius="lg"
                                bgcolor="white"
                                height="100%"
                            >
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <Icon color="info">collections</Icon>
                                    <MDTypography variant="h6" fontWeight="600" color="text.primary">Supporting Media</MDTypography>
                                    {isUploadingMedia && <CircularProgress size={16} color="info" sx={{ ml: 1 }} />}
                                </MDBox>

                                <MDBox
                                    sx={{
                                        p: 1,
                                        border: '1px dashed',
                                        borderColor: 'grey.300',
                                        borderRadius: 2,
                                        backgroundColor: 'grey.50',
                                        textAlign: 'center',
                                        minHeight: '40px',
                                        width: "660px",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <MDTypography variant="button" color="text" display="block" mb={1}>
                                        Attach images or videos (Optional)
                                    </MDTypography>
                                    <MDButton
                                        variant="outlined"
                                        color="info"
                                        size="small"
                                        component="label"
                                        disabled={isUploadingMedia}
                                        sx={{ alignSelf: 'center', mb: 2 }}
                                    >
                                        Upload Images
                                        <input type="file" hidden multiple accept="image/*" onChange={handleMediaChange} />
                                    </MDButton>

                                    <Divider sx={{ my: 1, mb: 2 }}>

                                    </Divider>

                                    <MDBox display="flex" gap={1} px={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Paste Video URL (YouTube, Drive, etc.)"
                                            value={videoUrl}
                                            onChange={(e) => setVideoUrl(e.target.value)}
                                            sx={{ bgcolor: "white" }}
                                        />
                                        <MDButton
                                            variant="gradient"
                                            color="info"
                                            size="small"
                                            onClick={handleAddVideoUrl}
                                            disabled={!videoUrl.trim()}
                                        >
                                            Add Video
                                        </MDButton>
                                    </MDBox>
                                    <MDTypography variant="caption" color="text" sx={{ mt: 1, px: 2, display: 'block', fontStyle: 'italic' }}>
                                        Tip: You can paste links from Google Drive, YouTube, or other cloud storage.
                                    </MDTypography>

                                    {existingMediaUrls.length > 0 && (
                                        <MDBox mt={2} mb={1}>
                                            <MDTypography variant="caption" color="text" fontWeight="bold" display="block" textAlign="left" mb={1}>
                                                Existing Documents
                                            </MDTypography>
                                            <MDBox display="flex" flexWrap="wrap" gap={1.5} justifyContent="flex-start">
                                                {existingMediaUrls.map((m, index) => (
                                                    <MDBox key={`exist-${index}`} position="relative" sx={{ width: 80, height: 80 }}>
                                                        <MDBox
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                borderRadius: 1,
                                                                border: "1px solid",
                                                                borderColor: "grey.300",
                                                                overflow: "hidden",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "white",
                                                                cursor: "pointer"
                                                            }}
                                                            onClick={() => window.open(m.url, '_blank')}
                                                        >
                                                            {m.type === 'image' ? (
                                                                <img src={m.url} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : m.type === 'video' ? (
                                                                <PlayCircle color="info" sx={{ fontSize: 32 }} />
                                                            ) : m.type === 'youtube' ? (
                                                                <YouTube color="error" sx={{ fontSize: 32 }} />
                                                            ) : m.type === 'drive' ? (
                                                                <InsertDriveFile color="info" sx={{ fontSize: 32 }} />
                                                            ) : m.type === 'pdf' ? (
                                                                <PictureAsPdf color="error" sx={{ fontSize: 32 }} />
                                                            ) : (
                                                                <Link color="info" sx={{ fontSize: 32 }} />
                                                            )}
                                                        </MDBox>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                const newUrls = [...existingMediaUrls];
                                                                newUrls.splice(index, 1);
                                                                setExistingMediaUrls(newUrls);
                                                            }}
                                                            sx={{
                                                                position: "absolute",
                                                                top: -8,
                                                                right: -8,
                                                                bgcolor: "error.main",
                                                                color: "white",
                                                                padding: "2px",
                                                                "&:hover": { bgcolor: "error.dark" }
                                                            }}
                                                        >
                                                            <Close sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </MDBox>
                                                ))}
                                            </MDBox>
                                        </MDBox>
                                    )}

                                    {formik.values.supportingMedia.length > 0 && (
                                        <MDBox mt={2}>
                                            <MDTypography variant="caption" color="text" fontWeight="bold" display="block" textAlign="left" mb={1}>
                                                New Documents
                                            </MDTypography>
                                            <MDBox display="flex" flexWrap="wrap" gap={1.5} justifyContent="flex-start">
                                                {formik.values.supportingMedia.map((file, index) => (
                                                    <MDBox key={index} position="relative" sx={{ width: 80, height: 80 }}>
                                                        <MDBox
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                borderRadius: 1,
                                                                border: "1px solid",
                                                                borderColor: "success.main",
                                                                overflow: "hidden",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "success.50",
                                                                cursor: "pointer"
                                                            }}
                                                            onClick={() => window.open(file.url, '_blank')}
                                                        >
                                                            {file.type === 'image' ? (
                                                                <img src={file.url} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : file.type === 'video' ? (
                                                                <PlayCircle color="success" sx={{ fontSize: 32 }} />
                                                            ) : file.type === 'youtube' ? (
                                                                <YouTube color="error" sx={{ fontSize: 32 }} />
                                                            ) : file.type === 'drive' ? (
                                                                <InsertDriveFile color="info" sx={{ fontSize: 32 }} />
                                                            ) : file.type === 'pdf' ? (
                                                                <PictureAsPdf color="error" sx={{ fontSize: 32 }} />
                                                            ) : (
                                                                <Link color="info" sx={{ fontSize: 32 }} />
                                                            )}
                                                        </MDBox>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeMedia(index)}
                                                            sx={{
                                                                position: "absolute",
                                                                top: -8,
                                                                right: -8,
                                                                bgcolor: "error.main",
                                                                color: "white",
                                                                padding: "2px",
                                                                "&:hover": { bgcolor: "error.dark" }
                                                            }}
                                                        >
                                                            <Close sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                        <MDTypography variant="caption" sx={{ display: 'block', fontSize: '8px', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {file.name}
                                                        </MDTypography>
                                                    </MDBox>
                                                ))}
                                            </MDBox>
                                        </MDBox>
                                    )}
                                </MDBox>
                            </MDBox>
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider />

                {/* Footer */}
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <MDButton
                        variant="text"
                        onClick={handleCloseUploadDialog}
                        disabled={formik.isSubmitting}
                        sx={{
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        Cancel
                    </MDButton>
                    {console.log('errors', formik.errors)}
                    <MDButton
                        variant="contained"
                        color="success"
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting || isUploadingReport || isUploadingMedia}
                        sx={{
                            textTransform: 'none',
                            px: 3,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 1,
                            },
                        }}
                    >
                        {formik.isSubmitting ? (
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} color="inherit" />
                                <span>{isEditMode ? "Updating..." : "Uploading..."}</span>
                            </MDBox>
                        ) : (
                            isEditMode ? "Update Report" : "Submit Report"
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(viewPdfUrl)} onClose={handleClosePdf} maxWidth="lg" fullWidth>
                <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Document Preview</Typography>
                    <IconButton onClick={handleClosePdf}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: "80vh" }}>
                    {viewPdfUrl && (
                        <iframe
                            src={viewPdfUrl}
                            width="100%"
                            height="100%"
                            style={{ border: "none" }}
                            title="Document Preview"
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar
                open={notification.open || Boolean(message.status)}
                autoHideDuration={3000}
                onClose={() => {
                    setNotification({ ...notification, open: false });
                    setMessage({ status: "", title: "", msg: "" });
                }}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => {
                        setNotification({ ...notification, open: false });
                        setMessage({ status: "", title: "", msg: "" });
                    }}
                    severity={message.status || notification.severity || "info"}
                    variant="filled"
                    sx={{ width: "100%", color: "white" }}
                >
                    {message.msg || notification.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default WeeklyReportManager;
